/**
 * Custom hooks for distillery operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { distilleryService } from '../services/distilleryService';

/**
 * Hook to fetch paginated distilleries
 * @param {Object} options - Query options
 * @returns {Object} Query result with data, loading, and error states
 */
export function useDistilleries(options = {}) {
  const { page = 1, limit = 20, sortBy = 'distillery_name', sortOrder = 'asc', filters = {} } = options;

  return useQuery({
    queryKey: ['distilleries', { page, limit, sortBy, sortOrder, filters }],
    queryFn: () => distilleryService.getDistilleries({ page, limit, sortBy, sortOrder, filters }),
    placeholderData: (previousData) => previousData
  });
}

/**
 * Hook to fetch all distilleries for dropdowns
 * @returns {Object} Query result with all distilleries
 */
export function useAllDistilleries() {
  return useQuery({
    queryKey: ['distilleries', 'all'],
    queryFn: () => distilleryService.getAllDistilleries(),
    staleTime: 1000 * 60 * 5
  });
}

/**
 * Hook to fetch a single distillery
 * @param {string} id - Distillery UUID
 * @returns {Object} Query result with distillery data
 */
export function useDistillery(id) {
  return useQuery({
    queryKey: ['distillery', id],
    queryFn: () => distilleryService.getDistilleryById(id),
    enabled: !!id
  });
}

/**
 * Hook to create a new distillery
 * @returns {Object} Mutation result with mutate function
 */
export function useCreateDistillery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: distilleryService.createDistillery,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distilleries'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }
  });
}

/**
 * Hook to update an existing distillery
 * @returns {Object} Mutation result with mutate function
 */
export function useUpdateDistillery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }) => distilleryService.updateDistillery(id, updates),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['distilleries'] });
      queryClient.invalidateQueries({ queryKey: ['distillery', id] });
    }
  });
}

/**
 * Hook to delete a distillery
 * @returns {Object} Mutation result with mutate function
 */
export function useDeleteDistillery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: distilleryService.deleteDistillery,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distilleries'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }
  });
}
